import React from "react";
import EbDialog from "../ExecutionBlueprintLiveReview/_shared/EbDialog";
import "./ConfirmStepModal.scss";

// ═════════════════════════════════════════════════════════════════════════════
// ConfirmStepModal — the confirmation in front of each Live Review stage.
//
// ⚠️ REBUILT ON `EbDialog` (2026-08-31). The public API is unchanged, so no call
// site moved; what changed is everything below the props.
//
// It used to be a react-bootstrap Modal with its own 680px / 24px-radius shell,
// a 100px illustrated icon with a tick badge, a 26px centred title and a
// space-between footer with ← → arrows. That was one of SEVEN dialog designs
// across the two apps. `EbDialog` is the one shell now; see its header for what
// the audit found and which value won each argument.
//
// Three things this lost, each on purpose:
//
//   - **The 100px icon and its badge.** It was illustration, and on a laptop it
//     pushed the actual question below the fold. A 44px tone chip does the same
//     job in a tenth of the space.
//   - **The centred 26px title.** Centred text works under a big centred
//     illustration and nowhere else; left-aligned at 18px, the eye finds each
//     line without hunting.
//   - **The ← → arrows.** They implied a wizard. Two of the three stages are a
//     point of no return, not a step forward.
//
// And one thing it GAINED, which is the reason this matters beyond looks: while
// the confirm is in flight the dialog can no longer be dismissed. Every preset
// here fronts a permanent lock, and the old shell let you Escape out of one
// mid-request.
// ═════════════════════════════════════════════════════════════════════════════

const CheckRingIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
    <path
      d="M8.2 12.3 10.8 15l5-5.4"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// One glyph per stage, sized for the 44px chip. Inlined rather than imported:
// this file is byte-identical across two repos whose icon modules sit at
// different paths, which is the same rule every `_shared/` file follows.
const LockIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect
      x="4.5"
      y="10.5"
      width="15"
      height="10"
      rx="2.2"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7"
      stroke="currentColor"
      strokeWidth="1.7"
    />
  </svg>
);

const ConnectionsIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="5" r="2.4" stroke="currentColor" strokeWidth="1.7" />
    <circle
      cx="5.5"
      cy="18.5"
      r="2.4"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <circle
      cx="18.5"
      cy="18.5"
      r="2.4"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M10.6 7.1 6.9 16.3M13.4 7.1l3.7 9.2M8 18.5h8"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);

const CalendarIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <rect
      x="3.5"
      y="5.5"
      width="17"
      height="15"
      rx="2.2"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M3.5 10h17M8.5 3.2v4M15.5 3.2v4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * @param icon        The stage's own glyph, rendered in the 44px chip.
 * @param title       The question.
 * @param subtitle    What confirming means. Presets state the consequence.
 * @param stepNumber/stepTitle/stepText   The "up next" block, for a stage that
 *                    has one. Omit for a terminal stage.
 * @param doneTitle/doneText   The terminal stage's version of that block.
 * @param confirmLabel/backLabel   Button copy.
 * @param confirming  In flight. Locks dismissal and spins the primary.
 * @param onConfirm/onBack/onClose
 *
 * ⚠️ `iconSize` is accepted and ignored. It sized the old 100px illustration;
 * the chip is one size now. Kept in the signature so the presets that pass it
 * don't have to change and no call site breaks.
 */
const ConfirmStepModal = ({
  icon,
  iconSize, // eslint-disable-line no-unused-vars
  title,
  subtitle,
  stepNumber,
  stepTitle,
  stepText,
  doneTitle,
  doneText,
  confirmLabel = "Confirm & Continue Next Step",
  // Shown in place of `confirmLabel` while the request is in flight. Separate
  // from it so the resting label doesn't have to be rebuilt by a ternary at
  // every call site — that pattern is what let a spinner and a label disagree.
  loadingLabel,
  backLabel = "Go back",
  confirming = false,
  onConfirm,
  onBack,
  onClose,
}) => (
  <EbDialog
    isOpen
    size="md"
    tone="primary"
    icon={icon}
    title={title}
    subtitle={subtitle}
    busy={confirming}
    onClose={onClose || onBack}
    actions={[
      { label: backLabel, variant: "ghost", onClick: onBack },
      {
        label: confirmLabel,
        loadingLabel,
        variant: "primary",
        onClick: onConfirm,
        loading: confirming,
        autoFocus: true,
      },
    ]}
  >
    {/* Naming what comes next is the point of this dialog — it is the only
        place either party sees the journey's shape while confirming a stage. */}
    {stepTitle ? (
      <div className="ebd-panel csm-next">
        <span className="ebd-panel-label">Up next</span>
        <div className="csm-next-row">
          <span className="csm-next-num">{stepNumber}</span>
          <div>
            <p className="csm-next-title">{stepTitle}</p>
            {stepText ? <p className="csm-next-text">{stepText}</p> : null}
          </div>
        </div>
      </div>
    ) : null}

    {/* The terminal stage's version: it reports completion rather than naming
        a next step. Mutually exclusive with the block above in practice. */}
    {doneTitle ? (
      <div className="ebd-panel csm-done">
        <span className="csm-done-icon" aria-hidden="true">
          <CheckRingIcon />
        </span>
        <div>
          <p className="csm-done-title">{doneTitle}</p>
          {doneText ? <p className="csm-done-text">{doneText}</p> : null}
        </div>
      </div>
    ) : null}
  </EbDialog>
);

// Copy is verbatim from the three frames. Spread onto ConfirmStepModal at the
// call site: <ConfirmStepModal {...CONFIRM_BLUEPRINT_REVIEW} onConfirm={…} />
export const CONFIRM_BLUEPRINT_REVIEW = {
  icon: <LockIcon />,
  title: "Finish the Blueprint Review?",
  // ⚠️ THIS SUBTITLE DID NOT SAY THE LOCK WAS PERMANENT (fixed 2026-09-03).
  // It read "You're confirming the reviewed BHAG and all ... objectives as
  // finalized" — which describes a filing action, not a one-way door. Three
  // faults in one line: no mention of permanence or of there being no undo, on
  // the dialog that performs the item-value lock; "objectives", which is not
  // this product's noun (they are WIGs / items); and "finalized", US spelling
  // against nine uses of "finalised" elsewhere in the feature.
  subtitle:
    "This locks the blueprint permanently — item values can't change afterwards, by either of you. There's no undo.",
  stepNumber: 2,
  stepTitle: "WIG Connections",
  stepText:
    "Map how your BHAG, Financial, Customer, Operations and People WIGs influence and depend on one another.",
};

export const CONFIRM_WIG_CONNECTIONS = {
  icon: <ConnectionsIcon />,
  title: "Confirm WIG Connections?",
  subtitle:
    "You're confirming the reviewed relationships between the BHAG and all Financial, Customer, Operations and People WIGs.",
  stepNumber: 3,
  stepTitle: "Prioritised WIGs for the next 12 months",
  // Same correction as `LiveReviewStepperModal`'s stage 3: the Year 1 screen's
  // priority number was derived from row position, never a server field, and
  // the 2026-09-03 redesign removed it from the UI. Leaving it here would have
  // left the two "up next" descriptions of one stage disagreeing.
  stepText:
    "Prioritise the WIGs for the next 12 months, and give each one an owner.",
};

// Stage 3, from node 33831:49650. No `stepNumber`/`stepTitle`/`stepText`: there
// is no stage 4, and the design replaces the up-next block with the completion
// note rather than leaving the slot empty.
//
// `confirmLabel` is the design's own, and it promises something the coach API
// cannot currently do - there is no lock endpoint on the coach side at all (see
// COACH-API.md §4, "No lock endpoint"). That's a contract gap to raise, not a
// copy change to make here: the design is the design.
export const CONFIRM_YEAR_ONE_PLAN = {
  icon: <CalendarIcon />,
  title: "Confirm your prioritised WIGs?",
  // Matches the Year 1 screen's override, so the old wording ("Year 1",
  // "priority order" — there is no ordering) can't resurface without it.
  subtitle:
    "This locks the whole blueprint: every WIG, the 12-month priorities and their owners. Nobody can change it afterwards.",
  doneTitle: "This completes the live review.",
  doneText:
    "The 12-month WIGs become the starting point for the CEO's Annual OKRs.",
  confirmLabel: "Lock blueprint",
};

export default ConfirmStepModal;
